/**
 * ISSUE #1 FIX: Safe stringify wrapper to prevent React "Objects are not valid as React child" errors
 * 
 * This utility safely converts any value (including objects, arrays, errors) to a string
 * that can be safely rendered in React components or passed to toast notifications.
 */

/**
 * Safely converts any value to a string for React rendering
 * @param {any} value - The value to stringify
 * @param {string} fallback - Fallback message if value can't be stringified
 * @returns {string} - Safe string representation
 */
export const safeStringify = (value, fallback = 'An error occurred') => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return fallback;
  }

  // Handle primitive types (string, number, boolean)
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // Handle Error objects
  if (value instanceof Error) {
    return value.message || fallback;
  }

  // Handle objects with 'detail' property (FastAPI error responses)
  if (typeof value === 'object' && value.detail) {
    return typeof value.detail === 'string' ? value.detail : JSON.stringify(value.detail);
  }

  // Handle objects with 'message' property
  if (typeof value === 'object' && value.message) {
    return typeof value.message === 'string' ? value.message : JSON.stringify(value.message);
  }

  // Handle objects with 'error' property
  if (typeof value === 'object' && value.error) {
    return typeof value.error === 'string' ? value.error : JSON.stringify(value.error);
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(item => safeStringify(item, '')).filter(Boolean).join(', ') || fallback;
  }

  // Handle objects - try JSON stringify as last resort
  if (typeof value === 'object') {
    try {
      const stringified = JSON.stringify(value);
      // Don't return empty objects
      if (stringified === '{}' || stringified === '[]') {
        return fallback;
      }
      return stringified;
    } catch (e) {
      return fallback;
    }
  }

  return fallback;
};

/**
 * Extracts error message from Axios error response
 * @param {Error} error - Axios error object
 * @param {string} fallback - Fallback message
 * @returns {string} - Safe error message
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  // Check for Axios error response
  if (error.response?.data) {
    return safeStringify(error.response.data, fallback);
  }

  // Check for error message
  if (error.message) {
    return error.message;
  }

  // Fallback
  return safeStringify(error, fallback);
};

/**
 * Safe wrapper for toast messages
 * @param {Function} toastFn - Toast function (toast.error, toast.success, etc.)
 * @param {any} message - Message to display
 * @param {object} options - Toast options
 */
export const safeToast = (toastFn, message, options = {}) => {
  const safeMessage = safeStringify(message);
  toastFn(safeMessage, options);
};
